import { useEffect, useState } from 'react';
import { useChat } from '../../contexts/ChatContext';
import { chatService } from '../../services/chatApi';
import Avatar from './Avatar';

const ROLE_LABELS = {
  owner: 'Owner',
  admin: 'Admin',
};

/**
 * Everyone in the current conversation.
 *
 * Fetched rather than read from the conversation row: the list endpoint caps
 * participants at 12 to keep that payload small, which is fine for a name
 * summary and wrong for a shepherd group of thirty.
 */
export default function ParticipantList() {
  const { activeConversationId } = useChat();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!activeConversationId) return undefined;

    let cancelled = false;
    setLoading(true);
    chatService
      .listParticipants(activeConversationId)
      .then((response) => { if (!cancelled) { setMembers(response.data || []); setError(null); } })
      .catch((err) => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [activeConversationId]);

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center text-sm text-primary/40">
        Loading members…
      </div>
    );
  }

  if (error) {
    return <p className="p-4 text-sm text-alert">{error}</p>;
  }

  return (
    <div className="flex-1 overflow-y-auto">
      <p className="px-4 py-3 text-xs font-semibold uppercase tracking-wide text-primary/50
                    border-b border-neutral-300 bg-highlight/30">
        {members.length} {members.length === 1 ? 'member' : 'members'}
      </p>

      <ul className="divide-y divide-neutral-300">
        {members.map((participant) => (
          <li key={participant.id} className="flex items-center gap-3 px-4 py-2.5">
            <Avatar
              name={participant.displayName}
              photoUrl={participant.photoUrl}
              seed={participant.id}
            />

            <span className="flex-1 min-w-0 text-sm text-primary truncate">
              {participant.displayName}
              {participant.isYou && <span className="text-primary/40"> (you)</span>}
            </span>

            {ROLE_LABELS[participant.role] && (
              <span className="px-2 py-0.5 rounded-full bg-highlight text-[10px] font-semibold
                               uppercase tracking-wide text-primary/60 shrink-0">
                {ROLE_LABELS[participant.role]}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
